import { useContext, useEffect, useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { Context } from './AuthContext';
import Spinner from './components/Spinner';

const ALLOWED_SUBSCRIPTION_STATUSES = new Set(['active', 'trialing', 'free']);

const RequireAuth = () => {
  const { user, authLoading } = useContext(Context);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [hasSubscriptionAccess, setHasSubscriptionAccess] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(undefined);

  useEffect(() => {
    let cancelled = false;

    async function checkSubscription() {
      if (!user) {
        if (!cancelled) {
          setHasSubscriptionAccess(true);
          setSubscriptionLoading(false);
        }
        return;
      }

      try {
        const token = await user.getIdToken();
        const response = await fetch(
          process.env.REACT_APP_FLASK_WEBSERVER + 'subscription_details',
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Subscription check failed with status ${response.status}`);
        }

        const res = await response.json();
        const status = res?.data?.subscription_status;

        if (!cancelled) {
          setSubscriptionStatus(status);
          setHasSubscriptionAccess(ALLOWED_SUBSCRIPTION_STATUSES.has(status));
        }
      } catch (error) {
        console.error('Failed to verify subscription status', error);
        if (!cancelled) {
          setHasSubscriptionAccess(true);
        }
      } finally {
        if (!cancelled) {
          setSubscriptionLoading(false);
        }
      }
    }

    setSubscriptionLoading(true);
    checkSubscription();

    return () => {
      cancelled = true;
    };
  }, [user]);

  if (authLoading || subscriptionLoading) {
    return (
      <div
        style={{
          height: '100vh',
          width: '100vw',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Spinner />
      </div>
    );
  }

  if (!user) {
    window.location.replace('https://www.simple-platform.com');
    return null;
  }

  if (!hasSubscriptionAccess) {
    if (!subscriptionStatus) {
      return <Navigate to="/select-plan?legacy=true" replace />;
    }
    return <Navigate to="/subscription-expired" replace />;
  }

  return <Outlet context={{ subscriptionStatus }} />;
};

export default RequireAuth;
